import { useMemo, useState } from "react";
import { Link, useLocation, useOutletContext } from "react-router-dom";

import { ShellSubrail } from "../components/shell/ShellSubrail";
import { WorkbenchLayout } from "../components/shell/WorkbenchLayout";
import type { ShellOutletContext } from "../nav/AppShell";
import { fetchCheckpointSetupList } from "../data/checkpoint-setup";
import { fetchClientSiteSetup } from "../data/clients-sites";
import { fetchEditableRoutes } from "../data/routes";
import { sitesForPicker } from "../lib/setup-site-options";
import { useReadModel } from "../data/useReadModel";
import { CheckpointEditor } from "../surfaces/CheckpointEditor";
import { ClientContactEditor } from "../surfaces/ClientContactEditor";
import { ClientSiteEditor } from "../surfaces/ClientSiteEditor";
import { PropertyMapEditor } from "../surfaces/PropertyMapEditor";
import { RouteEditor } from "../surfaces/RouteEditor";
import { SiteAssetEditor } from "../surfaces/SiteAssetEditor";
import { SiteBoundaryEditor } from "../surfaces/SiteBoundaryEditor";
import { TokenMintSessionProvider } from "../surfaces/token-mint-session";

// Setup workbench (console shell v3, Wave 1) - the onboarding chain as subrail sections
// instead of one mega-scroll: client -> site -> route -> checkpoints -> tokens. One section
// shows at a time (the board's "One section at a time"); all stay mounted so an in-progress
// form or an open one-time mint kit survives a section hop. Long lists scroll inside their
// pane, and the head metrics answer where the chain stands without scrolling.

export const SETUP_WB_SECTIONS = [
  { key: "map", id: "setup-property-map", label: "Property map" },
  { key: "clients", id: "setup-clients", label: "Clients & sites" },
  { key: "contacts", id: "setup-contacts", label: "Contacts" },
  { key: "routes", id: "setup-routes", label: "Routes" },
  { key: "checkpoints", id: "setup-checkpoints", label: "Checkpoints" },
  { key: "layers", id: "setup-boundaries", label: "Map layers" },
] as const;

export type SetupWbSectionKey = (typeof SETUP_WB_SECTIONS)[number]["key"];

/** Legacy in-page anchors (ladder era) resolve to the section that now owns them. */
const LEGACY_ANCHOR_SECTIONS: Readonly<Record<string, SetupWbSectionKey>> = {
  "setup-property-map": "map",
  "setup-clients": "clients",
  "setup-contacts": "contacts",
  "setup-routes": "routes",
  "setup-checkpoints": "checkpoints",
  "setup-boundaries": "layers",
  "setup-assets": "layers",
};

export function SetupPage() {
  const { client, config, can } = useOutletContext<ShellOutletContext>();
  const location = useLocation();
  const [active, setActive] = useState<SetupWbSectionKey>(() => {
    const anchor = location.hash.replace(/^#/, "");
    return LEGACY_ANCHOR_SECTIONS[anchor] ?? "clients";
  });
  // PRD-001I follow-up 1b: pins with no route (walk drafts, promoted pins) mint from the
  // checkpoint editor. Same mount rule as PatrolsPage: the session provider (confirm + kit
  // modals + sessionStorage ledger) exists only for tokens.mint holders.
  const canMintTokens = can("tokens.mint");
  const {
    data: setup,
    reload: reloadSetup,
  } = useReadModel(() => fetchClientSiteSetup(client, config.accountId), [client, config.accountId]);
  const pickerSites = useMemo(
    () => sitesForPicker(setup?.sites ?? [], setup?.clients ?? []),
    [setup],
  );
  const { data: allRoutes, reload: reloadRoutes } = useReadModel(
    () => fetchEditableRoutes(client, config.accountId),
    [client, config.accountId],
  );
  const activeRoutes = useMemo(
    () => (allRoutes ?? []).filter((route) => route.active),
    [allRoutes],
  );
  const { data: checkpointRows } = useReadModel(
    () => fetchCheckpointSetupList(client, config.accountId),
    [client, config.accountId],
  );

  const checkpointEditor = (
    <CheckpointEditor
      client={client}
      accountId={config.accountId}
      canManage={can("schedule.manage")}
      canMint={canMintTokens}
      mapTilerKey={config.mapTilerKey}
      pickerRoutes={activeRoutes}
      managedSites={setup?.sites ?? []}
    />
  );

  const workbench = (
    <WorkbenchLayout
      className="wb--panes setup-page"
      subrail={
        <ShellSubrail
          eyebrow="Workspace setup"
          items={SETUP_WB_SECTIONS.map(({ key, label }) => ({ key, label }))}
          activeKey={active}
          onSelect={(key) => setActive(key as SetupWbSectionKey)}
        />
      }
      head={
        <div className="wb-head">
          <div className="wb-head__row">
            <h1 className="wb-head__title">Setup</h1>
            <p className="wb-head__note">
              The property chain, bottom up: clients group sites, sites hold routes, routes group
              checkpoints into patrols. Templates and schedules attach to what you build here.
            </p>
          </div>
          <dl className="wb-metrics" aria-label="Setup progress">
            <div className="wb-metric">
              <dt className="wb-metric__label">Clients</dt>
              <dd className="wb-metric__value">{setup?.clients.length ?? 0}</dd>
            </div>
            <div className="wb-metric">
              <dt className="wb-metric__label">Sites</dt>
              <dd className="wb-metric__value">{setup?.sites.length ?? 0}</dd>
            </div>
            <div className="wb-metric">
              <dt className="wb-metric__label">Active routes</dt>
              <dd className="wb-metric__value">{activeRoutes.length}</dd>
            </div>
            <div className="wb-metric">
              <dt className="wb-metric__label">Checkpoints</dt>
              <dd className="wb-metric__value">{checkpointRows?.length ?? 0}</dd>
            </div>
          </dl>
        </div>
      }
    >
      <section
        id="setup-property-map"
        className="wb-section setup__section setup__section--map"
        aria-label="Property map"
        hidden={active !== "map"}
      >
        <div className="wb-section__intro">
          <h2 className="wb-section__title">Property map</h2>
          <p className="page__note">
            {can("schedule.manage")
              ? "Draw the site footprint and click to place checkpoint stops on a satellite map. Replaces lat/lng typing and GeoJSON paste for day-to-day setup."
              : "View property boundaries and checkpoint pins on the map (read-only)."}{" "}
            <Link className="setup__property-map-escape" to="/setup/property-map">
              Open fullscreen
            </Link>
          </p>
        </div>
        <div className="shell-pane wb-section__pane setup__map-pane">
          <div className="shell-pane__scroll wb-section__scroll setup__map-scroll">
            <PropertyMapEditor
              key={config.accountId}
              client={client}
              accountId={config.accountId}
              canManage={can("schedule.manage")}
              {...(config.mapTilerKey !== undefined ? { mapTilerKey: config.mapTilerKey } : {})}
              pickerSites={pickerSites}
              managedSites={setup?.sites ?? []}
            />
          </div>
        </div>
      </section>

      <section
        id="setup-clients"
        className="wb-section setup__section"
        aria-label="Clients and sites"
        hidden={active !== "clients"}
      >
        <div className="wb-section__intro">
          <h2 className="wb-section__title">Clients &amp; sites</h2>
          <p className="page__note">
            {can("schedule.manage")
              ? "Create the client (organization), then add at least one site under it. Sites hold the street address and timezone; routes and schedules use the site, not the client name."
              : "Clients and their sites for this account (read-only)."}
          </p>
        </div>
        <div className="shell-pane wb-section__pane">
          <div className="shell-pane__scroll wb-section__scroll">
            <ClientSiteEditor
              client={client}
              accountId={config.accountId}
              canManage={can("schedule.manage")}
              {...(config.mapTilerKey !== undefined ? { mapTilerKey: config.mapTilerKey } : {})}
              onSetupChanged={reloadSetup}
            />
          </div>
        </div>
      </section>

      <section
        id="setup-contacts"
        className="wb-section setup__section"
        aria-label="Client contacts"
        hidden={active !== "contacts"}
      >
        <div className="wb-section__intro">
          <h2 className="wb-section__title">Client contacts</h2>
          <p className="page__note">
            {can("client_profile.manage")
              ? "Optional: primary contacts, alert recipients, and guard-visible phone/email for each client or site."
              : "Client and site contact profiles for this account (read-only)."}
          </p>
        </div>
        <div className="shell-pane wb-section__pane">
          <div className="shell-pane__scroll wb-section__scroll">
            <ClientContactEditor
              client={client}
              accountId={config.accountId}
              canManage={can("client_profile.manage")}
              setup={setup}
            />
          </div>
        </div>
      </section>

      <section
        id="setup-routes"
        className="wb-section setup__section"
        aria-label="Routes"
        hidden={active !== "routes"}
      >
        <div className="wb-section__intro">
          <h2 className="wb-section__title">Routes</h2>
          <p className="page__note">
            {can("schedule.manage")
              ? "Pick a site from Clients & sites, then name the patrol loop. One site can have many routes (AM shift, PM shift, etc.)."
              : "Patrol routes for this account (read-only)."}
          </p>
        </div>
        <div className="shell-pane wb-section__pane">
          <div className="shell-pane__scroll wb-section__scroll">
            <RouteEditor
              client={client}
              accountId={config.accountId}
              canManage={can("schedule.manage")}
              pickerSites={pickerSites}
              onRoutesChanged={reloadRoutes}
            />
          </div>
        </div>
      </section>

      <section
        id="setup-checkpoints"
        className="wb-section setup__section"
        aria-label="Checkpoints"
        hidden={active !== "checkpoints"}
      >
        <div className="wb-section__intro">
          <h2 className="wb-section__title">Checkpoints</h2>
          <p className="page__note">
            {can("schedule.manage")
              ? "Add scan stops to each route. Use address lookup to place them on the map. Choose QR/NFC for posted tags, GPS geofence for drive-by vehicle patrols, or manual when tags are impractical. Use Mint QR on QR-preferred rows and Mint kit on NFC-preferred rows (or Replace material) to open the one-time print/export kit. Write NFC tags with an external writer; the console does not write blank tags in the browser."
              : "Each route's checkpoints in patrol order (read-only)."}
          </p>
        </div>
        <div className="shell-pane wb-section__pane">
          <div className="shell-pane__scroll wb-section__scroll">{checkpointEditor}</div>
        </div>
      </section>

      <section
        id="setup-boundaries"
        className="wb-section setup__section"
        aria-label="Site boundaries and asset zones"
        hidden={active !== "layers"}
      >
        <div className="wb-section__intro">
          <h2 className="wb-section__title">Map layers</h2>
          <p className="page__note">
            {can("schedule.manage")
              ? "Optional: import GeoJSON polygons for the property line and for labeled zones inside it (buildings, parking, gates). Both render on the Command Center map under checkpoints."
              : "Property boundaries and asset zones for this account's sites (read-only)."}
          </p>
        </div>
        {/* Peer editors sit 2-up above 1200 (board frame 07): same anatomy, same weight. */}
        <div className="wb-cols wb-cols--2 wb-section__cols">
          <div className="shell-pane wb-section__pane">
            <div className="shell-pane__head">
              <span className="shell-pane__title">Site boundaries</span>
            </div>
            <div className="shell-pane__scroll wb-section__scroll">
              <SiteBoundaryEditor
                client={client}
                accountId={config.accountId}
                canManage={can("schedule.manage")}
                pickerSites={pickerSites}
              />
            </div>
          </div>
          <div className="shell-pane wb-section__pane" id="setup-assets">
            <div className="shell-pane__head">
              <span className="shell-pane__title">Asset zones</span>
            </div>
            <div className="shell-pane__scroll wb-section__scroll">
              <SiteAssetEditor
                client={client}
                accountId={config.accountId}
                canManage={can("schedule.manage")}
                pickerSites={pickerSites}
              />
            </div>
          </div>
        </div>
      </section>
    </WorkbenchLayout>
  );

  return canMintTokens ? (
    <TokenMintSessionProvider client={client} accountId={config.accountId} enabled>
      {workbench}
    </TokenMintSessionProvider>
  ) : (
    workbench
  );
}
